import { BarChart, type Bar } from '@/components/charts/BarChart';
import { ChartEmpty, ChartFrame, ChartSkeleton } from '@/components/charts/ChartFrame';
import { LineChart } from '@/components/charts/LineChart';
import type { Round, RoundSummary } from '@/lib/api/types';
import { fmtDecimal, fmtToPar } from '@/lib/format/number';
import { MIN_ROUNDS_FOR_HANDICAP } from '@/lib/stats/aggregate';
import { axisMonths, handicapSeries, toParSeries, MIN_ROUNDS_FOR_SERIES } from '@/lib/stats/series';
import { t } from '@/lib/i18n';
import styles from './Stats.module.css';

/**
 * Os dois gráficos que a tela de Stats e o perfil de outro jogador dividem.
 *
 * Saíram do `Stats.tsx` quando o perfil (canvas 3b) passou a mostrar "Evolução do handicap" e
 * "Score para o par" — os mesmos dois desenhos, sobre as voltas de outra pessoa. Ficam nesta pasta,
 * e não em `components/`, porque continuam usando o `Stats.module.css`: o estilo é o daquela tela,
 * o que mudou foi de quem são as voltas.
 *
 * ## `person`
 *
 * O estado vazio é a única parte que sabe de quem está falando. "Você tem 3 voltas" está certo na
 * sua tela e errado no perfil de um amigo, então a pessoa gramatical entra por prop em vez de ficar
 * congelada no componente.
 */
export type ChartPerson = 'you' | 'them';

function haveLabel(person: ChartPerson, count: number): string {
  return person === 'you' ? t('stats.youHave', { count }) : t('stats.theyHave', { count });
}

export function HandicapChart({
  rounds,
  ready,
  progress,
  person = 'you',
}: {
  rounds: Round[];
  ready: boolean;
  progress?: string;
  person?: ChartPerson;
}) {
  const points = handicapSeries(rounds);
  const first = points[0]?.value;
  const last = points[points.length - 1]?.value;

  return (
    <ChartFrame
      title={t('stats.handicapChart')}
      subtitle={t('stats.handicapChartSub')}
      aside={
        points.length >= MIN_ROUNDS_FOR_SERIES && first != null && last != null ? (
          <span>
            {fmtDecimal(first)} → <strong className={styles.gold}>{fmtDecimal(last)}</strong> em{' '}
            {points.length} voltas
          </span>
        ) : undefined
      }
      xLabels={points.length >= MIN_ROUNDS_FOR_SERIES ? axisMonths(points) : undefined}
    >
      {!ready ? (
        <ChartSkeleton progress={progress} />
      ) : points.length < MIN_ROUNDS_FOR_SERIES ? (
        <ChartEmpty
          shape="line"
          requirement={t('stats.needRoundsWithHandicap', { count: MIN_ROUNDS_FOR_SERIES })}
          have={
            points.length === 0
              ? t('stats.handicapStartsAt', { count: MIN_ROUNDS_FOR_HANDICAP })
              : haveLabel(person, points.length)
          }
        />
      ) : (
        <LineChart
          ariaLabel={t('stats.handicapAria', {
            from: fmtDecimal(first),
            to: fmtDecimal(last),
            count: points.length,
          })}
          series={[
            {
              values: points.map((p) => p.value),
              color: 'var(--green-mid)',
              area: true,
              dot: () => ({ r: 3.2, fill: 'var(--green-mid)' }),
            },
          ]}
        />
      )}
    </ChartFrame>
  );
}

export function ToParChart({
  rounds,
  person = 'you',
}: {
  rounds: RoundSummary[];
  person?: ChartPerson;
}) {
  const points = toParSeries(rounds);
  const mean =
    points.length > 0 ? points.reduce((a, p) => a + p.value, 0) / points.length : null;

  const bars: Bar[] = points.map((p) => ({
    key: p.id,
    value: p.value,
    display: fmtToPar(p.value),
    label: '',
    // Bar colour carries nothing here: the chart is one round per bar, and a red bar would
    // read as a verdict the axis already delivers.
    tone: 'plain' as const,
  }));

  const enough = points.length >= MIN_ROUNDS_FOR_SERIES;

  return (
    <ChartFrame
      title={t('stats.toPar')}
      subtitle={t('stats.toParSub')}
      // The mean is silenced with the chart. Printing "média +14" beside an empty box would
      // be stating a figure the screen has just declared it does not have enough data for.
      aside={
        enough && mean != null ? (
          <span>{t('stats.averageAside', { value: fmtToPar(Math.round(mean)) })}</span>
        ) : undefined
      }
      xLabels={enough ? axisMonths(points, 2) : undefined}
    >
      {!enough ? (
        <ChartEmpty
          requirement={t('stats.needRounds', { count: MIN_ROUNDS_FOR_SERIES })}
          have={haveLabel(person, points.length)}
        />
      ) : (
        <div className={styles.barPad}>
          <BarChart
            bars={bars}
            height={104}
            referenceValue={mean ?? undefined}
            ariaLabel={t('stats.toParAria', {
              count: points.length,
              mean: fmtToPar(Math.round(mean ?? 0)),
            })}
            compact
          />
        </div>
      )}
    </ChartFrame>
  );
}

